import { globalFetchPolicy } from "@/components/layout/fetchPolicies"
import { useOrganizationsIdsQuery } from "@/graphql"
import { PageHydratedMark } from "@/helpers/pageHydrated"
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics"
import { usePageMeta } from "@/hooks/usePageMeta"
import BackToTopButton from "@/pages/website-landing/components/back-to-top-button/BackToTopButton"
import Footer from "@/pages/website-landing/components/footer/Footer"
import Header from "@/pages/website-landing/components/header/Header"
import WebsiteLandingLoader from "@/pages/website-landing/components/website-landing-loader/WebsiteLandingLoader"
import { websiteRoutes } from "@/router/routes"
import { EASETECH } from "@/utilities/global-variables"
import { Layout, message } from "antd"
import { FC, Suspense, useEffect } from "react"
import { Outlet, useLocation, useParams } from "react-router"
import "../../styles/tooltip.less"
import ModalDarkProvider from "../context/ModalDarkProvider"
import "./index.less"

const WebsiteLayout: FC = () => {
  const { org } = useParams()
  const { pathname } = useLocation()
  const { website } = websiteRoutes

  // Initialize and track route changes with Google Analytics
  // The hook has guards to prevent multiple initializations
  useGoogleAnalytics()
  usePageMeta()

  // Easetech's own pages (and the root, which has no org) always have a header: drawn at once, and in the prerendered
  // HTML, instead of after a request.
  const isEasetechSite = !org || org.toLowerCase() === EASETECH
  const { data } = useOrganizationsIdsQuery({
    fetchPolicy: globalFetchPolicy,
    variables: {
      filter: { slug: { eqi: org } },
    },
    skip: isEasetechSite,
    onError: error => message.error(error.message),
  })

  const isOrg = isEasetechSite || !!data?.organizations?.data?.length
  const currentLocation = pathname.split("/")[2]
  const isThankYouPage = `/${currentLocation}` === website.thankYouPage

  useEffect(() => {
    localStorage.setItem("current-org-slug", org || EASETECH)
  }, [org])

  return (
    <ModalDarkProvider>
      <Layout>
        <BackToTopButton />

        {isOrg && <Header />}

        <Suspense fallback={<WebsiteLandingLoader />}>
          <Outlet />
          <PageHydratedMark />
        </Suspense>
        {((org === EASETECH && !isThankYouPage) || !org) && <Footer />}
      </Layout>
    </ModalDarkProvider>
  )
}

export { WebsiteLayout }
